// Tester activity.  /analytics/testers
//
// Who signed in to the console, who ran test conversations, and how many
// conversations arrived on each platform, per IST day. Built so the team can
// follow up with the people they handed logins to.
//
// Sign-ins are recorded from the day tracking shipped (the server says when);
// Test Console history is complete, because every simulated conversation has
// always carried the console user who started it.

import { http, qs } from "../api";
import { Badge, Banner, SkeletonRows, Table, useAsync, type Column } from "../ui";
import { StatTile } from "../charts";
import { RangeControls, nf, stamp, useRange } from "./shared";

interface UserActivity {
  user_id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  created_at: string | null;
  logins: number;
  failed_logins: number;
  active_days: number;
  test_sessions: number;
  last_login: string | null;
  last_seen: string | null;
  last_test: string | null;
}

interface DayActivity {
  day: string;
  who: string[];
  logins: number;
  failed_logins: number;
  test_console: number;
  test_api: number;
  other_test: number;
  whatsapp: number;
  whatsapp_contacts: number;
}

interface TesterActivity {
  from: string;
  to: string;
  tracking_since: string | null;
  totals: {
    users_seen: number;
    logins: number;
    failed_logins: number;
    test_console: number;
    test_api: number;
    whatsapp: number;
  };
  users: UserActivity[];
  days: DayActivity[];
}

const count = (v: number) => (v ? <span className="num">{nf.format(v)}</span> : <span className="muted">0</span>);

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/** The most recent sign of life, and whether we can see it at all yet. */
function statusOf(u: UserActivity): { tone: "good" | "warning" | "neutral"; label: string } {
  if (!u.is_active) return { tone: "neutral", label: "Disabled" };
  if (u.last_seen || u.last_test) return { tone: "good", label: "Has used it" };
  return { tone: "warning", label: "No activity seen" };
}

export function TesterActivityView() {
  const { preset, setPreset, draft, setDraft, apply, range } = useRange({
    initial: "30days",
    fallback: "30days",
    presetKey: "testers.preset",
  });

  const query = useAsync(
    () => http.get<TesterActivity>(`/activity/testers${qs({ from: range.from, to: range.to })}`),
    [range.from, range.to]
  );
  const data = query.data;

  const userColumns: Column<UserActivity>[] = [
    {
      key: "email",
      header: "User",
      sortable: true,
      render: (u) => (
        <div>
          <div className="strong">{u.name || u.email}</div>
          {u.name && <div className="muted">{u.email}</div>}
        </div>
      ),
    },
    { key: "role", header: "Role", sortable: true },
    {
      key: "last_seen",
      header: "Status",
      render: (u) => {
        const s = statusOf(u);
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    { key: "last_login", header: "Last sign-in", sortable: true, render: (u) => stamp(u.last_login) },
    { key: "last_seen", header: "Last active", sortable: true, render: (u) => stamp(u.last_seen) },
    { key: "last_test", header: "Last test", sortable: true, render: (u) => stamp(u.last_test) },
    { key: "logins", header: "Sign-ins", align: "right", sortable: true, render: (u) => count(u.logins) },
    {
      key: "failed_logins",
      header: "Failed sign-ins",
      align: "right",
      sortable: true,
      render: (u) =>
        u.failed_logins ? <Badge tone="warning">{nf.format(u.failed_logins)}</Badge> : <span className="muted">0</span>,
    },
    { key: "active_days", header: "Active days", align: "right", sortable: true, render: (u) => count(u.active_days) },
    { key: "test_sessions", header: "Test sessions", align: "right", sortable: true, render: (u) => count(u.test_sessions) },
  ];

  const dayColumns: Column<DayActivity>[] = [
    { key: "day", header: "Date", sortable: true, render: (d) => dayLabel(d.day) },
    {
      key: "who",
      header: "Who was active",
      render: (d) => (d.who.length ? d.who.join(", ") : <span className="muted">—</span>),
    },
    { key: "logins", header: "Sign-ins", align: "right", sortable: true, render: (d) => count(d.logins) },
    { key: "failed_logins", header: "Failed", align: "right", sortable: true, render: (d) => count(d.failed_logins) },
    { key: "test_console", header: "Test Console", align: "right", sortable: true, render: (d) => count(d.test_console) },
    { key: "test_api", header: "Test API (automated)", align: "right", sortable: true, render: (d) => count(d.test_api) },
    { key: "whatsapp", header: "WhatsApp (live number)", align: "right", sortable: true, render: (d) => count(d.whatsapp) },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Tester activity</h1>
          <div className="sub">Who signed in, who tested, and conversations per platform, by day (IST).</div>
        </div>
        <div className="page-actions">
          <RangeControls preset={preset} onPreset={setPreset} draft={draft} onDraft={setDraft} onApply={apply} />
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          Could not load tester activity: {query.error.message}
        </Banner>
      )}

      {data && (
        <Banner tone="info">
          {data.tracking_since
            ? `Sign-ins and daily activity are recorded since ${stamp(data.tracking_since)}. `
            : "Sign-in tracking has just been switched on; sign-ins will appear from now. "}
          Test Console sessions are complete history. WhatsApp (live number) counts every
          conversation on the real number, from customers and staff phones alike.
        </Banner>
      )}

      <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
        {query.loading || !data ? (
          [0, 1, 2, 3].map((i) => (
            <div className="card" key={i}>
              <SkeletonRows n={2} cols={2} />
            </div>
          ))
        ) : (
          <>
            <StatTile label="People active" value={data.totals.users_seen} />
            <StatTile label="Sign-ins" value={data.totals.logins} />
            <StatTile label="Test Console sessions" value={data.totals.test_console} />
            <StatTile label="WhatsApp conversations" value={data.totals.whatsapp} />
          </>
        )}
      </div>

      <div className="card flush" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="card-head">
          <h3>By user</h3>
        </div>
        <Table columns={userColumns} rows={data?.users ?? []} loading={query.loading} empty="No users" />
      </div>

      <div className="card flush">
        <div className="card-head">
          <h3>By day</h3>
        </div>
        <Table columns={dayColumns} rows={data?.days ?? []} loading={query.loading} empty="No activity in this period" />
      </div>
    </div>
  );
}
